// Catalogue des permissions d'employé : modules × actions.
// Source unique de vérité partagée par les deux espaces (Propriétaire, Agence).
// Les permissions effectives d'un employé sont stockées dans Supabase
// (employes_agence.permissions, jsonb) sous la forme { module: { action: bool } }.

export type PermissionAction = "voir" | "creer" | "modifier" | "supprimer";

export const PERMISSION_ACTIONS: PermissionAction[] = ["voir", "creer", "modifier", "supprimer"];

export const ACTION_LABELS: Record<PermissionAction, string> = {
  voir: "Voir",
  creer: "Créer",
  modifier: "Modifier",
  supprimer: "Supprimer",
};

export interface PermissionModuleDef {
  key: string;
  label: string;
  icon: string;
  actions: PermissionAction[];
  group: string;
}

const ALL_ACTIONS: PermissionAction[] = ["voir", "creer", "modifier", "supprimer"];

export const PERMISSION_MODULES: PermissionModuleDef[] = [
  { key: "tableau_bord", label: "Tableau de bord", icon: "ri-dashboard-3-line", actions: ["voir"], group: "Général" },
  { key: "proprietaires", label: "Propriétaires", icon: "ri-user-star-line", actions: ALL_ACTIONS, group: "Gestion" },
  { key: "immeubles", label: "Immeubles", icon: "ri-building-2-line", actions: ALL_ACTIONS, group: "Gestion" },
  { key: "biens", label: "Biens", icon: "ri-home-4-line", actions: ALL_ACTIONS, group: "Gestion" },
  { key: "locataires", label: "Locataires", icon: "ri-user-3-line", actions: ALL_ACTIONS, group: "Gestion" },
  { key: "contrats", label: "Contrats", icon: "ri-file-text-line", actions: ALL_ACTIONS, group: "Gestion" },
  { key: "loyers", label: "Loyers", icon: "ri-wallet-3-line", actions: ALL_ACTIONS, group: "Finances" },
  { key: "paiements", label: "Paiements", icon: "ri-hand-coin-line", actions: ALL_ACTIONS, group: "Finances" },
  { key: "impayes", label: "Impayés", icon: "ri-alert-line", actions: ["voir"], group: "Finances" },
  { key: "depenses", label: "Dépenses", icon: "ri-money-cny-circle-line", actions: ALL_ACTIONS, group: "Finances" },
  { key: "maintenance", label: "Maintenance", icon: "ri-tools-line", actions: ALL_ACTIONS, group: "Suivi" },
  { key: "documents", label: "Documents", icon: "ri-folder-line", actions: ALL_ACTIONS, group: "Suivi" },
  { key: "rapports", label: "Rapports", icon: "ri-file-chart-line", actions: ["voir"], group: "Suivi" },
  { key: "statistiques", label: "Statistiques", icon: "ri-bar-chart-box-line", actions: ["voir"], group: "Suivi" },
  { key: "employes", label: "Employés", icon: "ri-team-line", actions: ALL_ACTIONS, group: "Organisation" },
  { key: "parametres", label: "Paramètres", icon: "ri-settings-3-line", actions: ["voir"], group: "Organisation" },
];

export const PERMISSION_GROUPS = Array.from(new Set(PERMISSION_MODULES.map((module) => module.group)));

export const MODULE_LABELS: Record<string, string> = PERMISSION_MODULES.reduce(
  (acc, module) => ({ ...acc, [module.key]: module.label }),
  {} as Record<string, string>
);

export const MODULE_ICONS: Record<string, string> = PERMISSION_MODULES.reduce(
  (acc, module) => ({ ...acc, [module.key]: module.icon }),
  {} as Record<string, string>
);

/** Carte des permissions : { module: { action: boolean } }. */
export type PermissionMap = Record<string, Partial<Record<PermissionAction, boolean>>>;

/** "module.action" → booléen, robuste si l'entrée est malformée. */
export function hasPermission(map: PermissionMap | null | undefined, permission: string): boolean {
  if (!map) return false;
  const [module, action] = permission.split(".");
  if (!module) return false;
  if (!action) return Boolean(map[module]?.voir);
  return Boolean(map[module]?.[action as PermissionAction]);
}

/** Construit une carte où chaque module listé reçoit toutes ses actions. */
export function buildMapFromModules(moduleKeys: string[]): PermissionMap {
  const map: PermissionMap = {};
  PERMISSION_MODULES.forEach((module) => {
    if (moduleKeys.includes(module.key)) {
      map[module.key] = module.actions.reduce(
        (acc, action) => ({ ...acc, [action]: true }),
        {} as Partial<Record<PermissionAction, boolean>>
      );
    }
  });
  return map;
}

/** Carte minimale : uniquement le tableau de bord visible. */
export function minimalMap(): PermissionMap {
  return buildMapFromModules(["tableau_bord"]);
}

/** Accès complet : tous les modules, toutes les actions. */
export function fullAccessMap(): PermissionMap {
  return buildMapFromModules(PERMISSION_MODULES.map((module) => module.key));
}

/** Nombre de permissions actives. */
export function countPermissions(map: PermissionMap | null | undefined): number {
  if (!map) return 0;
  return Object.values(map).reduce(
    (total, actions) => total + Object.values(actions ?? {}).filter(Boolean).length,
    0
  );
}

/** Compte les modules ayant au moins une permission active. */
export function countModules(map: PermissionMap | null | undefined): number {
  if (!map) return 0;
  return Object.values(map).filter((actions) => Object.values(actions ?? {}).some(Boolean)).length;
}

/** Rôles employés par défaut (modèles proposés au DG), configurables ensuite. */
export interface RoleTemplate {
  code: string;
  nom: string;
  permissions: PermissionMap;
}

export const DEFAULT_ROLE_TEMPLATES: RoleTemplate[] = [
  { code: "responsable", nom: "Responsable", permissions: fullAccessMap() },
  {
    code: "gestionnaire",
    nom: "Gestionnaire",
    permissions: {
      ...buildMapFromModules(["tableau_bord", "proprietaires", "immeubles", "biens", "locataires", "contrats"]),
      loyers: { voir: true, creer: true, modifier: true },
      paiements: { voir: true, creer: true, modifier: true },
      impayes: { voir: true },
      depenses: { voir: true, creer: true, modifier: true },
      maintenance: { voir: true, creer: true, modifier: true },
      documents: { voir: true, creer: true },
      rapports: { voir: true },
      statistiques: { voir: true },
    },
  },
  {
    code: "comptable",
    nom: "Comptable",
    permissions: {
      tableau_bord: { voir: true },
      contrats: { voir: true },
      loyers: { voir: true, creer: true, modifier: true },
      paiements: { voir: true, creer: true, modifier: true },
      impayes: { voir: true },
      depenses: { voir: true, creer: true, modifier: true },
      documents: { voir: true },
      rapports: { voir: true },
      statistiques: { voir: true },
    },
  },
  {
    code: "commercial",
    nom: "Agent commercial",
    permissions: {
      tableau_bord: { voir: true },
      proprietaires: { voir: true, creer: true, modifier: true },
      immeubles: { voir: true },
      biens: { voir: true },
      locataires: { voir: true, creer: true, modifier: true },
      contrats: { voir: true, creer: true },
      rapports: { voir: true },
    },
  },
  {
    code: "maintenance",
    nom: "Agent de maintenance",
    permissions: {
      tableau_bord: { voir: true },
      immeubles: { voir: true },
      biens: { voir: true },
      maintenance: { voir: true, creer: true, modifier: true, supprimer: true },
      documents: { voir: true },
    },
  },
  {
    code: "assistant",
    nom: "Assistant(e)",
    permissions: {
      tableau_bord: { voir: true },
      proprietaires: { voir: true },
      immeubles: { voir: true },
      biens: { voir: true },
      locataires: { voir: true },
      contrats: { voir: true },
      documents: { voir: true },
    },
  },
  { code: "autre", nom: "Autre", permissions: minimalMap() },
];

export const EMPLOYEE_ROLE_OPTIONS = DEFAULT_ROLE_TEMPLATES.map((role) => ({
  value: role.code,
  label: role.nom,
}));

export function roleTemplate(code: string): RoleTemplate | undefined {
  return DEFAULT_ROLE_TEMPLATES.find((role) => role.code === code);
}

export function citizenRoleLabel(code: string): string {
  return DEFAULT_ROLE_TEMPLATES.find((role) => role.code === code)?.nom ?? (code || "Rôle non défini");
}

/** Rôles système qu'un DG ne peut JAMAIS attribuer à un employé. */
export const RESERVED_ROLES = ["super_admin", "dg_proprietaire", "dg_agence"];